/**
 * MaintenanceContext
 * 점검 상태를 앱 전역에서 관리하는 Context
 * 10초마다 점검 상태를 확인하여 즉시 반영
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getActiveMaintenance } from '../api/maintenanceApi';
import { ActiveMaintenanceResponse, DEFAULT_MAINTENANCE_RESPONSE } from '../types/maintenance';

/** 폴링 간격: 10초 */
const POLLING_INTERVAL = 10000;

interface MaintenanceContextType {
    /** 현재 활성화된 점검 정보 */
    activeMaintenance: ActiveMaintenanceResponse;
    /** 점검 중 여부 */
    isMaintenance: boolean;
    /** 로딩 상태 */
    loading: boolean;
    /** 점검 상태 즉시 재확인 (중요 액션 전 호출) */
    checkMaintenanceNow: () => Promise<boolean>;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

interface MaintenanceProviderProps {
    children: ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
    const [activeMaintenance, setActiveMaintenance] = useState<ActiveMaintenanceResponse>(DEFAULT_MAINTENANCE_RESPONSE);
    const [loading, setLoading] = useState(true);

    /**
     * 점검 상태 조회
     */
    const fetchMaintenance = useCallback(async (): Promise<boolean> => {
        try {
            const data = await getActiveMaintenance();
            setActiveMaintenance(data);
            return data.isMaintenance;
        } catch {
            // 에러 시 기본값 유지 (점검 아님)
            setActiveMaintenance(DEFAULT_MAINTENANCE_RESPONSE);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * 점검 상태 즉시 확인 (중요 액션 전 호출)
     * 캐시 없이 서버에서 직접 확인
     */
    const checkMaintenanceNow = useCallback(async (): Promise<boolean> => {
        try {
            const data = await getActiveMaintenance();
            setActiveMaintenance(data);
            return data.isMaintenance;
        } catch {
            // 에러 시 안전하게 점검 중으로 처리 (중요 액션 차단)
            return true;
        }
    }, []);

    useEffect(() => {
        // 초기 로드
        fetchMaintenance();

        // 10초마다 점검 상태 확인
        const intervalId = setInterval(fetchMaintenance, POLLING_INTERVAL);

        return () => clearInterval(intervalId);
    }, [fetchMaintenance]);

    return (
        <MaintenanceContext.Provider
            value={{
                activeMaintenance,
                isMaintenance: activeMaintenance.isMaintenance,
                loading,
                checkMaintenanceNow,
            }}
        >
            {children}
        </MaintenanceContext.Provider>
    );
};

/**
 * 점검 상태 Context 사용 훅
 */
export const useMaintenanceContext = (): MaintenanceContextType => {
    const context = useContext(MaintenanceContext);
    if (!context) {
        throw new Error('useMaintenanceContext must be used within MaintenanceProvider');
    }
    return context;
};

export default MaintenanceProvider;
